import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import chatService from "../../services/chatService";
import instituteService from "../../services/instituteService";
import api from "../../services/api";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Campaign,
  Group,
  Business,
  School,
  Class,
  People,
  Send,
  Settings,
  PersonAdd,
} from "@mui/icons-material";
import { resolveUserDisplayName } from "../../utils/userDisplay";

const classLabel = (c) => {
  const name = c?.name || "";
  const section = c?.section || "";
  return section ? `${name} - ${section}` : name;
};

const scopeLabel = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "string") return v;
  if (typeof v === "object" && v.name) return v.name;
  return null;
};

const mapBroadcastApiToRow = (b) => ({
  id: b.id,
  name: b.name || "",
  description: b.description || "",
  level: b.level || "institute",
  instituteId: b.instituteId ?? null,
  institute: scopeLabel(b.institute),
  departmentId: b.departmentId ?? null,
  department: scopeLabel(b.department),
  classId: b.classId ?? null,
  class: scopeLabel(b.class) || (typeof b.class === "object" ? classLabel(b.class) : null),
  members: b.members ?? 0,
  admins: b.createdBy ? [b.createdBy] : [],
  allowTeacherPost: b.allowTeacherPost !== false,
  status: b.isActive === false ? "inactive" : "active",
  messages: 0,
  createdAt: b.createdAt ? String(b.createdAt).slice(0, 10) : "",
});

const DEFAULT_BROADCAST_FILTERS = { search: "", allowTeacherPost: "", level: "", instituteId: "" };
const DEFAULT_COMMUNITY_FILTERS = { search: "", level: "", instituteId: "" };

const mapCommunityApiToRow = (c) => ({
  id: c.id,
  name: c.name || "",
  description: c.description || "",
  level: c.level || "institute",
  instituteId: c.instituteId ?? null,
  institute: scopeLabel(c.institute),
  departmentId: c.departmentId ?? null,
  department: scopeLabel(c.department),
  classId: c.classId ?? null,
  class: scopeLabel(c.class) || (typeof c.class === "object" ? classLabel(c.class) : null),
  members: c.members ?? 0,
  admins: c.createdBy ? [c.createdBy] : [],
  accessType: "open",
  status: "active",
  createdAt: c.createdAt ? String(c.createdAt).slice(0, 10) : "",
});

const emptyScopeForm = () => ({
  level: "institute",
  instituteId: "",
  departmentId: "",
  classId: "",
});

const BroadcastManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [broadcastFilters, setBroadcastFilters] = useState(DEFAULT_BROADCAST_FILTERS);
  const [communityFilters, setCommunityFilters] = useState(DEFAULT_COMMUNITY_FILTERS);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendContent, setSendContent] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageKind, setManageKind] = useState("broadcast");
  const [manageForm, setManageForm] = useState({ id: null, name: "", description: "", allowTeacherPost: true });
  const [manageLoading, setManageLoading] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [membersTarget, setMembersTarget] = useState(null);
  const [membersKind, setMembersKind] = useState("community");
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberActionBusy, setMemberActionBusy] = useState(null);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteKind, setDeleteKind] = useState("broadcast");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [broadcastRows, setBroadcastRows] = useState([]);
  const [communityRows, setCommunityRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const silentFilterLoadRef = useRef(false);
  const memberSearchRequestIdRef = useRef(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    ...emptyScopeForm(),
    admins: [],
    allowTeacherPost: true,
  });
  const [institutes, setInstitutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [institutesLoading, setInstitutesLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);

  const buildBroadcastParams = useCallback((filters) => {
    const params = {};
    const q = (filters.search || "").trim();
    if (q) params.search = q;
    params.includeInactive = true;
    if (filters.allowTeacherPost === "true" || filters.allowTeacherPost === "false") {
      params.allowTeacherPost = filters.allowTeacherPost;
    }
    if (filters.level) params.level = filters.level;
    if (filters.instituteId) params.instituteId = filters.instituteId;
    return params;
  }, []);

  const buildCommunityParams = useCallback((filters) => {
    const params = {};
    const q = (filters.search || "").trim();
    if (q) params.search = q;
    if (filters.level) params.level = filters.level;
    if (filters.instituteId) params.instituteId = filters.instituteId;
    return params;
  }, []);

  const loadInstitutes = useCallback(async () => {
    setInstitutesLoading(true);
    try {
      const response = await instituteService.getInstitutes({ limit: 99999 });
      const list = response.data?.data?.institutes || response.data?.data || [];
      setInstitutes(Array.isArray(list) ? list : []);
    } catch {
      setInstitutes([]);
    } finally {
      setInstitutesLoading(false);
    }
  }, []);

  const loadDepartments = useCallback(async (instituteId) => {
    if (!instituteId) {
      setDepartments([]);
      return;
    }
    setDepartmentsLoading(true);
    try {
      const response = await instituteService.getInstituteDepartments(instituteId);
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : response.data?.data?.departments || [];
      setDepartments(Array.isArray(list) ? list : []);
    } catch {
      setDepartments([]);
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  const loadClasses = useCallback(async (instituteId, departmentId) => {
    if (!instituteId) {
      setClasses([]);
      return;
    }
    setClassesLoading(true);
    try {
      const params = { instituteId, limit: 99999 };
      if (departmentId) params.departmentId = departmentId;
      const response = await api.get("/class/get-all-classes", { params });
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data) ? response.data : [];
      setClasses(Array.isArray(list) ? list : []);
    } catch {
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInstitutes();
  }, [loadInstitutes]);

  useEffect(() => {
    if (!createDialogOpen) return;
    if (formData.instituteId) void loadDepartments(formData.instituteId);
    else setDepartments([]);
  }, [createDialogOpen, formData.instituteId, loadDepartments]);

  useEffect(() => {
    if (!createDialogOpen) return;
    if (formData.instituteId && (formData.level === "class" || formData.departmentId)) {
      void loadClasses(formData.instituteId, formData.level === "class" ? formData.departmentId : "");
    } else setClasses([]);
  }, [createDialogOpen, formData.instituteId, formData.departmentId, formData.level, loadClasses]);

  const loadBroadcasts = useCallback(async (filters = broadcastFilters, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const res = await chatService.getBroadcastGroups(buildBroadcastParams(filters));
      const payload = res?.data?.data ?? res?.data ?? [];
      setBroadcastRows(Array.isArray(payload) ? payload.map(mapBroadcastApiToRow) : []);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to load broadcast groups";
      setLoadError(msg);
      setBroadcastRows([]);
    } finally {
      setLoading(false);
    }
  }, [broadcastFilters, buildBroadcastParams]);

  const loadCommunity = useCallback(async (filters = communityFilters, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const res = await chatService.getCommunityGroups(buildCommunityParams(filters));
      const payload = res?.data?.data ?? res?.data ?? [];
      setCommunityRows(Array.isArray(payload) ? payload.map(mapCommunityApiToRow) : []);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to load community groups";
      setLoadError(msg);
      setCommunityRows([]);
    } finally {
      setLoading(false);
    }
  }, [communityFilters, buildCommunityParams]);

  const handleTabChange = useCallback((_, v) => {
    setActiveTab(v);
    if (v === 0) setBroadcastFilters({ ...DEFAULT_BROADCAST_FILTERS });
    else setCommunityFilters({ ...DEFAULT_COMMUNITY_FILTERS });
  }, []);

  useEffect(() => {
    if (activeTab !== 0) return;
    const t = setTimeout(() => {
      const silent = silentFilterLoadRef.current;
      silentFilterLoadRef.current = false;
      void loadBroadcasts(broadcastFilters, { silent });
    }, 350);
    return () => clearTimeout(t);
  }, [activeTab, broadcastFilters, loadBroadcasts]);

  useEffect(() => {
    if (activeTab !== 1) return;
    const t = setTimeout(() => {
      const silent = silentFilterLoadRef.current;
      silentFilterLoadRef.current = false;
      void loadCommunity(communityFilters, { silent });
    }, 350);
    return () => clearTimeout(t);
  }, [activeTab, communityFilters, loadCommunity]);

  const patchAllowTeacherPost = useCallback(
    async (rowId, checked) => {
      setRowBusyId(rowId);
      setLoadError(null);
      try {
        await chatService.updateBroadcastGroup(rowId, { allowTeacherPost: checked });
        await loadBroadcasts(broadcastFilters);
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to update";
        setLoadError(msg);
      } finally {
        setRowBusyId(null);
      }
    },
    [loadBroadcasts, broadcastFilters]
  );

  const patchBroadcastActive = useCallback(
    async (rowId, active) => {
      setRowBusyId(rowId);
      setLoadError(null);
      try {
        await chatService.updateBroadcastGroup(rowId, { isActive: !!active });
        await loadBroadcasts(broadcastFilters);
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to update";
        setLoadError(msg);
      } finally {
        setRowBusyId(null);
      }
    },
    [loadBroadcasts, broadcastFilters]
  );

  const openSendDialog = useCallback((row) => {
    setSendTarget(row);
    setSendContent("");
    setSendDialogOpen(true);
  }, []);

  const submitSendMessage = useCallback(async () => {
    if (!sendTarget?.id) return;
    const text = (sendContent || "").trim();
    if (!text) return;
    setSendLoading(true);
    setLoadError(null);
    try {
      await chatService.sendBroadcastMessage(sendTarget.id, { content: text });
      setSendDialogOpen(false);
      setSendTarget(null);
      setSendContent("");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to send";
      setLoadError(msg);
    } finally {
      setSendLoading(false);
    }
  }, [sendTarget, sendContent]);

  const openManageDialog = useCallback((row, kind) => {
    setManageKind(kind);
    setManageForm({
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      allowTeacherPost: row.allowTeacherPost !== false,
    });
    setManageDialogOpen(true);
  }, []);

  const loadGroupMembers = useCallback(async (groupId, kind) => {
    setMembersLoading(true);
    try {
      const res =
        kind === "broadcast"
          ? await chatService.getBroadcastGroupMembers(groupId)
          : await chatService.getCommunityGroupMembers(groupId);
      const list = res?.data?.data ?? res?.data ?? [];
      setMembersList(Array.isArray(list) ? list : []);
    } catch (e) {
      setMembersList([]);
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to load members";
      setLoadError(msg);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const openMembersDialog = useCallback(
    (row, kind) => {
      setMembersKind(kind);
      setMembersTarget(row);
      setMemberSearch("");
      setMemberSearchResults([]);
      setSelectedUsersToAdd([]);
      setMembersDialogOpen(true);
      void loadGroupMembers(row.id, kind);
    },
    [loadGroupMembers]
  );

  // Debounced user search for add-member (scoped to group institute; abort stale responses)
  useEffect(() => {
    if (!membersDialogOpen) return undefined;
    const q = (memberSearch || "").trim();
    if (q.length < 2) {
      setMemberSearchResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const requestId = Date.now();
    memberSearchRequestIdRef.current = requestId;
    const timer = setTimeout(async () => {
      setMemberSearchLoading(true);
      try {
        const params = { search: q, limit: 30 };
        const instId = membersTarget?.instituteId ?? membersTarget?.institute?.id;
        if (instId != null && instId !== "") params.instituteId = instId;
        const res = await chatService.getChatUsers(params);
        if (memberSearchRequestIdRef.current !== requestId) return;
        const list = res?.data?.data ?? res?.data ?? [];
        const existingIds = new Set(membersList.map((m) => Number(m.userId ?? m.user?.id)));
        const qLower = q.toLowerCase();
        setMemberSearchResults(
          (Array.isArray(list) ? list : [])
            .filter((u) => !existingIds.has(Number(u.id)))
            .filter((u) => {
              const hay = `${u.firstName || ""} ${u.lastName || ""} ${u.fullName || ""} ${u.email || ""}`.toLowerCase();
              return hay.includes(qLower);
            })
        );
      } catch {
        if (memberSearchRequestIdRef.current === requestId) setMemberSearchResults([]);
      } finally {
        if (memberSearchRequestIdRef.current === requestId) setMemberSearchLoading(false);
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [memberSearch, membersDialogOpen, membersList, membersTarget]);

  const submitAddMembers = useCallback(async () => {
    const groupId = membersTarget?.id;
    if (!groupId || !selectedUsersToAdd.length) return;
    setMemberActionBusy("add");
    setLoadError(null);
    try {
      const userIds = selectedUsersToAdd.map((u) => u.id);
      if (membersKind === "broadcast") {
        await chatService.addBroadcastGroupMembers(groupId, userIds);
      } else {
        await chatService.addCommunityGroupMembers(groupId, userIds);
      }
      setSelectedUsersToAdd([]);
      setMemberSearch("");
      setMemberSearchResults([]);
      await loadGroupMembers(groupId, membersKind);
      if (membersKind === "broadcast") await loadBroadcasts(broadcastFilters);
      else await loadCommunity(communityFilters);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to add members";
      setLoadError(msg);
    } finally {
      setMemberActionBusy(null);
    }
  }, [
    membersTarget,
    selectedUsersToAdd,
    membersKind,
    loadGroupMembers,
    loadBroadcasts,
    loadCommunity,
    broadcastFilters,
    communityFilters,
  ]);

  const submitRemoveMember = useCallback(
    async (userId) => {
      const groupId = membersTarget?.id;
      if (!groupId || userId == null) return;
      setMemberActionBusy(userId);
      setLoadError(null);
      try {
        if (membersKind === "broadcast") {
          await chatService.removeBroadcastGroupMember(groupId, userId);
        } else {
          await chatService.removeCommunityGroupMember(groupId, userId);
        }
        await loadGroupMembers(groupId, membersKind);
        if (membersKind === "broadcast") await loadBroadcasts(broadcastFilters);
        else await loadCommunity(communityFilters);
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to remove member";
        setLoadError(msg);
      } finally {
        setMemberActionBusy(null);
      }
    },
    [membersTarget, membersKind, loadGroupMembers, loadBroadcasts, loadCommunity, broadcastFilters, communityFilters]
  );

  const submitManage = useCallback(async () => {
    const id = manageForm.id;
    if (!id) return;
    const name = (manageForm.name || "").trim();
    if (!name) return;
    setManageLoading(true);
    setLoadError(null);
    try {
      if (manageKind === "broadcast") {
        await chatService.updateBroadcastGroup(id, {
          name,
          description: (manageForm.description || "").trim(),
          allowTeacherPost: !!manageForm.allowTeacherPost,
        });
      } else {
        await chatService.updateCommunityGroup(id, {
          name,
          description: (manageForm.description || "").trim(),
        });
      }
      setManageDialogOpen(false);
      if (manageKind === "broadcast") await loadBroadcasts(broadcastFilters);
      else await loadCommunity(communityFilters);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to save";
      setLoadError(msg);
    } finally {
      setManageLoading(false);
    }
  }, [manageForm, manageKind, loadBroadcasts, loadCommunity, broadcastFilters, communityFilters]);

  const openDeleteDialog = useCallback((row, kind) => {
    setDeleteTarget(row);
    setDeleteKind(kind);
    setDeleteDialogOpen(true);
  }, []);

  const submitDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    setLoadError(null);
    try {
      if (deleteKind === "broadcast") {
        await chatService.deleteBroadcastGroup(deleteTarget.id);
      } else {
        await chatService.deleteCommunityGroup(deleteTarget.id);
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setViewDialogOpen(false);
      setViewTarget(null);
      if (deleteKind === "broadcast") await loadBroadcasts(broadcastFilters);
      else await loadCommunity(communityFilters);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to delete";
      setLoadError(msg);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, deleteKind, loadBroadcasts, loadCommunity, broadcastFilters, communityFilters]);

  const totalMembers = useMemo(() => {
    const sum = (arr) => arr.reduce((acc, r) => acc + (Number(r.members) || 0), 0);
    return sum(broadcastRows) + sum(communityRows);
  }, [broadcastRows, communityRows]);

  const getLevelIcon = (level) => {
    switch (level) {
      case "institute":
        return <Business />;
      case "department":
        return <School />;
      case "class":
        return <Class />;
      default:
        return <Group />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "institute":
        return "primary";
      case "department":
        return "secondary";
      case "class":
        return "success";
      default:
        return "default";
    }
  };

  const broadcastColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Broadcast Group",
        flex: 1,
        minWidth: 250,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: `${getLevelColor(params.row.level)}.main`,
                width: 40,
                height: 40,
              }}
            >
              <Campaign />
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {params.row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((params.row.description || "").substring(0, 40) || "—")}
                {(params.row.description || "").length > 40 ? "…" : ""}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "level",
        headerName: "Level",
        width: 120,
        renderCell: (params) => (
          <Chip
            icon={getLevelIcon(params.value)}
            label={(params.value || "institute").charAt(0).toUpperCase() + (params.value || "institute").slice(1)}
            size="small"
            color={getLevelColor(params.value)}
          />
        ),
      },
      {
        field: "institute",
        headerName: "Institute",
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => scopeLabel(params.row.institute) || "—",
      },
      {
        field: "members",
        headerName: "Members",
        width: 100,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <People fontSize="small" />
            {(params.value ?? 0).toLocaleString()}
          </Box>
        ),
      },
      {
        field: "messages",
        headerName: "Messages",
        width: 100,
      },
      {
        field: "allowTeacherPost",
        headerName: "Teacher Posts",
        width: 130,
        renderCell: (params) => (
          <Switch
            checked={!!params.value}
            size="small"
            disabled={loading || rowBusyId === params.row.id}
            onChange={(e) => {
              e.stopPropagation();
              void patchAllowTeacherPost(params.row.id, e.target.checked);
            }}
          />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => (
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                checked={params.value === "active"}
                size="small"
                disabled={loading || rowBusyId === params.row.id}
                onChange={(e) => {
                  e.stopPropagation();
                  void patchBroadcastActive(params.row.id, e.target.checked);
                }}
              />
            }
            label={params.value}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 210,
        renderCell: (params) => (
          <Box>
            <IconButton
              size="small"
              onClick={() => {
                setViewTarget({ ...params.row, _kind: "broadcast" });
                setViewDialogOpen(true);
              }}
            >
              <Visibility />
            </IconButton>
            <IconButton size="small" title="Manage members" onClick={() => openMembersDialog(params.row, "broadcast")}>
              <PersonAdd />
            </IconButton>
            <IconButton size="small" onClick={() => openSendDialog(params.row)}>
              <Send />
            </IconButton>
            <IconButton size="small" onClick={() => openManageDialog(params.row, "broadcast")}>
              <Settings />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => openDeleteDialog(params.row, "broadcast")}>
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ],
    [loading, rowBusyId, patchAllowTeacherPost, patchBroadcastActive, openSendDialog, openManageDialog, openDeleteDialog, openMembersDialog]
  );

  const communityColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Community Group",
        flex: 1,
        minWidth: 250,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ bgcolor: "info.main", width: 40, height: 40 }}>
              <Group />
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {params.row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((params.row.description || "").substring(0, 40) || "—")}
                {(params.row.description || "").length > 40 ? "…" : ""}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "level",
        headerName: "Level",
        width: 120,
        renderCell: (params) => (
          <Chip
            label={(params.value || "institute").charAt(0).toUpperCase() + (params.value || "institute").slice(1)}
            size="small"
            color={getLevelColor(params.value)}
          />
        ),
      },
      {
        field: "institute",
        headerName: "Institute",
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => scopeLabel(params.row.institute) || "—",
      },
      {
        field: "members",
        headerName: "Members",
        width: 100,
      },
      {
        field: "accessType",
        headerName: "Access",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={params.value === "open" ? "success" : "warning"}
          />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={params.value === "active" ? "success" : "default"}
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 180,
        renderCell: (params) => (
          <Box>
            <IconButton
              size="small"
              onClick={() => {
                setViewTarget({ ...params.row, _kind: "community" });
                setViewDialogOpen(true);
              }}
            >
              <Visibility />
            </IconButton>
            <IconButton size="small" title="Manage members" onClick={() => openMembersDialog(params.row, "community")}>
              <PersonAdd />
            </IconButton>
            <IconButton size="small" onClick={() => openManageDialog(params.row, "community")}>
              <Edit />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => openDeleteDialog(params.row, "community")}>
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ],
    [openManageDialog, openDeleteDialog, openMembersDialog]
  );

  const handleCreateBroadcast = async () => {
    const name = (formData.name || "").trim();
    if (!name) return;
    setSubmitLoading(true);
    setLoadError(null);
    try {
      const description = (formData.description || "").trim();
      const scope = {
        level: formData.level,
        instituteId: formData.instituteId,
        departmentId: formData.level === "institute" ? "" : formData.departmentId,
        classId: formData.level === "class" ? formData.classId : "",
      };
      if (activeTab === 0) {
        await chatService.createBroadcastGroup({
          name,
          description,
          allowTeacherPost: !!formData.allowTeacherPost,
          ...scope,
        });
      } else {
        await chatService.createCommunityGroup({ name, description, ...scope });
      }
      if (activeTab === 0) await loadBroadcasts(broadcastFilters);
      else await loadCommunity(communityFilters);
      setCreateDialogOpen(false);
      setFormData({
        name: "",
        description: "",
        ...emptyScopeForm(),
        admins: [],
        allowTeacherPost: true,
      });
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to create group";
      setLoadError(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const viewIsBroadcast = viewTarget?._kind === "broadcast";

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Broadcast & Community Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage broadcast groups and community channels across all levels
          </Typography>
        </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setFormData({
                name: "",
                description: "",
                ...emptyScopeForm(),
                admins: [],
                allowTeacherPost: true,
              });
              setCreateDialogOpen(true);
            }}
          >
          Create Group
        </Button>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Broadcasts",
            value: broadcastRows.length,
            color: "primary.main",
            icon: <Campaign />,
          },
          {
            label: "Community Groups",
            value: communityRows.length,
            color: "info.main",
            icon: <Group />,
          },
          {
            label: "Total Members",
            value: totalMembers.toLocaleString(),
            color: "success.main",
            icon: <People />,
          },
          {
            label: "Messages Today",
            value: "—",
            color: "warning.main",
            icon: <Send />,
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}
              >
                <Avatar sx={{ bgcolor: stat.color }}>{stat.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            icon={<Campaign />}
            label="Broadcast Groups"
            iconPosition="start"
          />
          <Tab icon={<Group />} label="Community Groups" iconPosition="start" />
        </Tabs>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              placeholder="Search groups..."
              size="small"
              value={activeTab === 0 ? broadcastFilters.search : communityFilters.search}
              onChange={(e) => {
                const v = e.target.value;
                if (activeTab === 0) setBroadcastFilters((f) => ({ ...f, search: v }));
                else setCommunityFilters((f) => ({ ...f, search: v }));
              }}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Level</InputLabel>
              <Select
                label="Level"
                value={activeTab === 0 ? broadcastFilters.level : communityFilters.level}
                onChange={(e) => {
                  const v = e.target.value;
                  if (activeTab === 0) setBroadcastFilters((f) => ({ ...f, level: v }));
                  else setCommunityFilters((f) => ({ ...f, level: v }));
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="institute">Institute</MenuItem>
                <MenuItem value="department">Department</MenuItem>
                <MenuItem value="class">Class</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={institutesLoading}>
              <InputLabel>Institute</InputLabel>
              <Select
                label="Institute"
                value={activeTab === 0 ? broadcastFilters.instituteId : communityFilters.instituteId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (activeTab === 0) setBroadcastFilters((f) => ({ ...f, instituteId: v }));
                  else setCommunityFilters((f) => ({ ...f, instituteId: v }));
                }}
              >
                <MenuItem value="">All</MenuItem>
                {institutes.map((inst) => (
                  <MenuItem key={inst.id} value={String(inst.id)}>
                    {inst.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {activeTab === 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Teacher posts</InputLabel>
                <Select
                  label="Teacher posts"
                  value={broadcastFilters.allowTeacherPost}
                  onChange={(e) => setBroadcastFilters((f) => ({ ...f, allowTeacherPost: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (activeTab === 0) void loadBroadcasts(broadcastFilters);
                else void loadCommunity(communityFilters);
              }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                silentFilterLoadRef.current = true;
                if (activeTab === 0) setBroadcastFilters({ ...DEFAULT_BROADCAST_FILTERS });
                else setCommunityFilters({ ...DEFAULT_COMMUNITY_FILTERS });
              }}
              disabled={loading}
            >
              Clear filters
            </Button>
          </Box>

          {activeTab === 0 ? (
            <DataGrid
              rows={broadcastRows}
              columns={broadcastColumns}
              autoHeight
              disableRowSelectionOnClick
              loading={loading}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              initialState={{
                pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } },
              }}
              sx={{ border: "none" }}
            />
          ) : (
            <DataGrid
              rows={communityRows}
              columns={communityColumns}
              autoHeight
              disableRowSelectionOnClick
              loading={loading}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              initialState={{
                pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } },
              }}
              sx={{ border: "none" }}
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setCreateDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Create {activeTab === 0 ? "Broadcast" : "Community"} Group
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  value={formData.level}
                  label="Level"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      level: e.target.value,
                      departmentId: "",
                      classId: "",
                    })
                  }
                >
                  <MenuItem value="institute">Institute Level</MenuItem>
                  <MenuItem value="department">Department Level</MenuItem>
                  <MenuItem value="class">Class Level</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={institutesLoading}>
                <InputLabel>Institute</InputLabel>
                <Select
                  value={formData.instituteId}
                  label="Institute"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      instituteId: e.target.value,
                      departmentId: "",
                      classId: "",
                    })
                  }
                >
                  {institutes.map((inst) => (
                    <MenuItem key={inst.id} value={String(inst.id)}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(formData.level === "department" ||
              formData.level === "class") && (
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!formData.instituteId || departmentsLoading}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={formData.departmentId}
                    label="Department"
                    onChange={(e) =>
                      setFormData({ ...formData, departmentId: e.target.value, classId: "" })
                    }
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={String(dept.id)}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.level === "class" && (
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!formData.departmentId || classesLoading}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={formData.classId}
                    label="Class"
                    onChange={(e) =>
                      setFormData({ ...formData, classId: e.target.value })
                    }
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls.id} value={String(cls.id)}>
                        {classLabel(cls)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {activeTab === 0 && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowTeacherPost}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowTeacherPost: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Allow Teachers to Post"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateBroadcast()}
            disabled={
              submitLoading ||
              !(formData.name || "").trim() ||
              !formData.instituteId ||
              (formData.level === "department" && !formData.departmentId) ||
              (formData.level === "class" && (!formData.departmentId || !formData.classId))
            }
          >
            {submitLoading ? <CircularProgress size={22} color="inherit" /> : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendDialogOpen}
        onClose={() => { setSendDialogOpen(false); setSendTarget(null); setSendContent(""); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send broadcast message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Message"
            value={sendContent}
            onChange={(e) => setSendContent(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSendDialogOpen(false); setSendTarget(null); }} disabled={sendLoading}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitSendMessage()} disabled={sendLoading || !(sendContent || "").trim()}>
            {sendLoading ? <CircularProgress size={22} color="inherit" /> : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit {manageKind === "broadcast" ? "broadcast" : "community"} group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={manageForm.name}
            onChange={(e) => setManageForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            minRows={2}
            value={manageForm.description}
            onChange={(e) => setManageForm((f) => ({ ...f, description: e.target.value }))}
            sx={{ mt: 2 }}
          />
          {manageKind === "broadcast" && (
            <FormControlLabel
              sx={{ mt: 2, display: "block" }}
              control={
                <Switch
                  checked={!!manageForm.allowTeacherPost}
                  onChange={(e) => setManageForm((f) => ({ ...f, allowTeacherPost: e.target.checked }))}
                />
              }
              label="Allow teachers to post"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)} disabled={manageLoading}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitManage()} disabled={manageLoading || !(manageForm.name || "").trim()}>
            {manageLoading ? <CircularProgress size={22} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete group?</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteKind === "broadcast" ? "Broadcast" : "Community"} &quot;{deleteTarget?.name}&quot; will be deactivated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void submitDelete()} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={22} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); setViewTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {viewIsBroadcast ? "Broadcast details" : "Community details"}
            <IconButton onClick={() => { setViewDialogOpen(false); setViewTarget(null); }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewTarget && (
            <Box>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: "auto",
                    mb: 1,
                    bgcolor: `${getLevelColor(viewTarget.level)}.main`,
                  }}
                >
                  {viewIsBroadcast ? <Campaign sx={{ fontSize: 40 }} /> : <Group sx={{ fontSize: 40 }} />}
                </Avatar>
                <Typography variant="h6">{viewTarget.name}</Typography>
                <Chip
                  label={(viewTarget.level || "institute").charAt(0).toUpperCase() + (viewTarget.level || "institute").slice(1)}
                  size="small"
                  color={getLevelColor(viewTarget.level)}
                  sx={{ mt: 1, textTransform: "capitalize" }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {viewTarget.description || "—"}
              </Typography>
              {viewIsBroadcast && (
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={viewTarget.allowTeacherPost !== false}
                      onChange={(e) => {
                        const next = { ...viewTarget, allowTeacherPost: e.target.checked };
                        setViewTarget(next);
                        void patchAllowTeacherPost(viewTarget.id, e.target.checked);
                      }}
                    />
                  }
                  label="Allow teachers to post"
                />
              )}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Institute
                  </Typography>
                  <Typography variant="body2">
                    {scopeLabel(viewTarget.institute) || "—"}
                  </Typography>
                </Grid>
                {viewTarget.department && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body2">
                      {viewTarget.department}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight={700} color="primary">
                    {(viewTarget.members ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption">Members</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight={700} color="info.main">
                    {viewTarget.messages ?? 0}
                  </Typography>
                  <Typography variant="caption">Messages</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="success.main"
                  >
                    {(viewTarget.admins || []).length}
                  </Typography>
                  <Typography variant="caption">Admins</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Admins
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {(viewTarget.admins || []).map((admin, i) => (
                  <Chip key={i} label={admin} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setViewDialogOpen(false); setViewTarget(null); }}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<PersonAdd />}
            onClick={() => {
              openMembersDialog(viewTarget, viewIsBroadcast ? "broadcast" : "community");
              setViewDialogOpen(false);
            }}
          >
            Members
          </Button>
          {viewIsBroadcast && (
            <Button variant="outlined" startIcon={<Send />} onClick={() => { openSendDialog(viewTarget); setViewDialogOpen(false); }}>
              Send message
            </Button>
          )}
          <Button variant="contained" startIcon={<Settings />} onClick={() => { openManageDialog(viewTarget, viewIsBroadcast ? "broadcast" : "community"); setViewDialogOpen(false); }}>
            Manage
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={membersDialogOpen}
        onClose={() => {
          if (memberActionBusy) return;
          setMembersDialogOpen(false);
          setMembersTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" component="span">
              Members — {membersTarget?.name || ""}
            </Typography>
            <IconButton
              onClick={() => {
                setMembersDialogOpen(false);
                setMembersTarget(null);
              }}
              disabled={!!memberActionBusy}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Add members
          </Typography>
          <Autocomplete
            multiple
            options={memberSearchResults}
            loading={memberSearchLoading}
            value={selectedUsersToAdd}
            onChange={(_, value) => setSelectedUsersToAdd(value)}
            inputValue={memberSearch}
            onInputChange={(_, value, reason) => {
              if (reason === "input" || reason === "clear") setMemberSearch(value);
            }}
            getOptionLabel={(option) =>
              resolveUserDisplayName(option, { fallback: option?.email || `User ${option?.id}` })
            }
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            filterOptions={(x) => x}
            noOptionsText={
              (memberSearch || "").trim().length < 2
                ? "Type at least 2 characters to search"
                : "No users found"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search users"
                placeholder="Name or email"
                size="small"
              />
            )}
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={memberActionBusy === "add" ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
            disabled={!selectedUsersToAdd.length || !!memberActionBusy}
            onClick={() => void submitAddMembers()}
            sx={{ mb: 2 }}
          >
            Add selected
          </Button>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Current members ({membersList.length})
          </Typography>
          {membersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : membersList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members yet. Search above to add people.
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 320, overflow: "auto" }}>
              {membersList.map((m) => {
                const uid = m.userId ?? m.user?.id;
                const name = resolveUserDisplayName(m.user || m, {
                  fallback: m.user?.email || `User ${uid}`,
                });
                return (
                  <ListItem key={m.id || uid} divider>
                    <ListItemAvatar>
                      <Avatar src={m.user?.profileImage || undefined}>{String(name).charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={name}
                      secondary={`${m.user?.email || ""}${m.role ? ` · ${m.role}` : ""}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        disabled={!!memberActionBusy}
                        onClick={() => void submitRemoveMember(uid)}
                        title="Remove member"
                      >
                        {memberActionBusy === uid ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMembersDialogOpen(false);
              setMembersTarget(null);
            }}
            disabled={!!memberActionBusy}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastManagement;
